import { REACTION_KINDS, type Reaction, type ReactionKind, type ReactionTotals } from './types';

/**
 * The reaction vocabulary, and the maths for putting reactions on a timeline.
 *
 * Six kinds, fixed by the API. The glyph is presentation only: every place a
 * reaction is drawn also carries `label`, because an emoji is not an
 * accessible name and "thumbs up sign" is not what a screen reader should be
 * reading out in the middle of a review.
 */

export interface ReactionMeta {
  /** Drawn, never announced. */
  glyph: string;
  /** The accessible name, and the word shown next to the glyph. */
  label: string;
}

export const REACTION_META: Record<ReactionKind, ReactionMeta> = {
  like: { glyph: '\u{1F44D}', label: 'Like' },
  love: { glyph: '❤️', label: 'Love' },
  laugh: { glyph: '\u{1F602}', label: 'Laugh' },
  wow: { glyph: '\u{1F62E}', label: 'Wow' },
  clap: { glyph: '\u{1F44F}', label: 'Applause' },
  think: { glyph: '\u{1F914}', label: 'Thinking' },
};

export function emptyReactionTotals(): ReactionTotals {
  return { like: 0, love: 0, laugh: 0, wow: 0, clap: 0, think: 0 };
}

/**
 * The API always sends all six keys, but a response is a wire format and this
 * is a public endpoint: normalising here means one missing key cannot turn a
 * count into `undefined` three components deep.
 */
export function normalizeTotals(input: Partial<Record<string, number>> | undefined): ReactionTotals {
  const totals = emptyReactionTotals();
  if (!input) return totals;
  for (const kind of REACTION_KINDS) {
    const value = input[kind];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      totals[kind] = Math.floor(value);
    }
  }
  return totals;
}

export function isReactionKind(value: string): value is ReactionKind {
  return (REACTION_KINDS as readonly string[]).includes(value);
}

export function totalReactionCount(totals: ReactionTotals): number {
  return REACTION_KINDS.reduce((sum, kind) => sum + totals[kind], 0);
}

/**
 * "6 love, 2 laugh". The calm, non-animated statement of what happened, which
 * is the version a screen reader gets and the version that survives
 * `prefers-reduced-motion`.
 */
export function totalsSummary(totals: ReactionTotals): string {
  const parts = REACTION_KINDS.filter((kind) => totals[kind] > 0).map(
    (kind) => `${totals[kind]} ${REACTION_META[kind].label.toLowerCase()}`,
  );
  return parts.join(', ');
}

// ---------------------------------------------------------------------------
// Timeline clustering
// ---------------------------------------------------------------------------

export interface ReactionCluster {
  /** Stable across renders so a cluster does not remount as one lands. */
  key: string;
  /** Where the marker sits, and where clicking it seeks to. */
  timecodeSec: number;
  count: number;
  /** Kinds inside this cluster, most frequent first. */
  breakdown: Array<{ kind: ReactionKind; count: number }>;
  /** The kind whose glyph represents the cluster. */
  topKind: ReactionKind;
  /** True while any member is still an unconfirmed optimistic write. */
  pending: boolean;
}

/** Reactions closer together than this collapse into one marker. */
const BASE_WINDOW_SEC = 0.35;

/**
 * A two second window is right for a three minute recording and useless for a
 * ninety minute one, where two seconds is a fraction of a pixel. The window
 * widens with the duration so a marker is never thinner than roughly one
 * percent of the bar, which is about the smallest thing worth aiming at.
 */
export function clusterWindowSec(durationSec: number): number {
  // Taps that would land on the same few pixels of the bar share a marker
  // with a count; anything further apart is its own. About one percent of
  // the timeline, never less than a third of a second.
  if (!Number.isFinite(durationSec) || durationSec <= 0) return BASE_WINDOW_SEC;
  return Math.max(BASE_WINDOW_SEC, durationSec * 0.01);
}

/**
 * Group reactions into markers. Input need not be sorted: an optimistic
 * reaction is appended at the end of the list regardless of its timecode.
 *
 * `isPending` lets the caller mark its own unconfirmed writes without the
 * wire type growing a client-only field.
 */
export function clusterReactions(
  reactions: readonly Reaction[],
  durationSec: number,
  isPending: (id: string) => boolean = () => false,
): ReactionCluster[] {
  if (reactions.length === 0) return [];

  const window = clusterWindowSec(durationSec);
  const sorted = [...reactions].sort((a, b) => a.timecodeSec - b.timecodeSec);
  const clusters: ReactionCluster[] = [];

  let members: Reaction[] = [];
  let anchor = sorted[0].timecodeSec;

  const flush = () => {
    if (members.length === 0) return;
    const counts = new Map<ReactionKind, number>();
    for (const member of members) {
      counts.set(member.kind, (counts.get(member.kind) ?? 0) + 1);
    }
    const breakdown = [...counts.entries()]
      .map(([kind, count]) => ({ kind, count }))
      .sort((a, b) => b.count - a.count || REACTION_KINDS.indexOf(a.kind) - REACTION_KINDS.indexOf(b.kind));

    clusters.push({
      key: `${anchor.toFixed(2)}:${members[0].id}`,
      timecodeSec: anchor,
      count: members.length,
      breakdown,
      topKind: breakdown[0].kind,
      pending: members.some((member) => isPending(member.id)),
    });
    members = [];
  };

  for (const reaction of sorted) {
    if (members.length > 0 && reaction.timecodeSec - anchor > window) {
      flush();
      anchor = reaction.timecodeSec;
    }
    if (members.length === 0) anchor = reaction.timecodeSec;
    members.push(reaction);
  }
  flush();

  return clusters;
}

/** "3 reactions at 1:24: 2 love, 1 laugh". The marker's accessible name. */
export function clusterLabel(cluster: ReactionCluster, timeText: string): string {
  const breakdown = cluster.breakdown
    .map(({ kind, count }) =>
      cluster.count === 1 ? REACTION_META[kind].label.toLowerCase() : `${count} ${REACTION_META[kind].label.toLowerCase()}`,
    )
    .join(', ');
  return cluster.count === 1
    ? `Jump to ${breakdown} at ${timeText}`
    : `Jump to ${cluster.count} reactions at ${timeText}: ${breakdown}`;
}
